import React from "react";
import { Button, Input, Alert } from "@components";
import { useController } from "./useController";

export const ResetPasswordPage: React.FC = () => {
    const {
        email,
        password,
        confirmPassword,
        isLoading,
        isSuccess,
        isVerifyingEmail,
        isEmailVerified,
        validationError,
        createPasswordError,
        verifyEmailError,
        setPassword,
        setConfirmPassword,
        onResetPassword,
    } = useController();

    // État de succès - Mot de passe réinitialisé
    if (isSuccess) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
                            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                            Mot de passe réinitialisé !
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            Votre mot de passe a été réinitialisé avec succès. Vous allez être redirigé vers la page de connexion.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // État de vérification de l'email
    if (isVerifyingEmail) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <div className="mx-auto h-12 w-12 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        </div>
                        <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                            Vérification en cours...
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            Vérification de votre code de réinitialisation
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // État d'erreur de vérification
    if (!isVerifyingEmail && verifyEmailError && !isEmailVerified) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
                            <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </div>
                        <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                            Lien invalide
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            {verifyEmailError.message || "Le lien de réinitialisation est invalide ou a expiré."}
                        </p>
                        <p className="mt-4 text-sm text-gray-500">
                            Veuillez demander un nouveau lien de réinitialisation.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Formulaire de réinitialisation (affiché seulement si l'email est vérifié avec succès)
    if (!isVerifyingEmail && isEmailVerified) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div>
                        <div className="text-center mb-6">
                            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
                                <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <p className="mt-2 text-sm text-green-600 font-medium">
                                Code vérifié avec succès !
                            </p>
                        </div>
                        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                            Créer un nouveau mot de passe
                        </h2>
                        <p className="mt-2 text-center text-sm text-gray-600">
                            Pour l'email: <span className="font-medium text-indigo-600">{email}</span>
                        </p>
                    </div>
                    
                    <div className="mt-8 space-y-6">
                        {(validationError || createPasswordError) && (
                            <Alert 
                                type="error" 
                                message={validationError || createPasswordError?.message || ""} 
                            />
                        )}
                        
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                    Nouveau mot de passe
                                </label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Entrez votre nouveau mot de passe"
                                    required
                                />
                            </div>
                            
                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                                    Confirmer le mot de passe
                                </label>
                                <Input
                                    id="confirmPassword"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirmez votre nouveau mot de passe"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <Button
                                onClick={onResetPassword}
                                disabled={isLoading || !password || !confirmPassword}
                                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? "Réinitialisation..." : "Réinitialiser le mot de passe"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // État par défaut (ne devrait pas arriver normalement)
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                        Chargement...
                    </h2>
                </div>
            </div>
        </div>
    );
};
